import { Component, ElementRef } from '@angular/core';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { HttpErrorResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';

import { PageService } from '../../../../../services/page.service';
import { ErrorMessageService } from '../../../../../services/error-message.service';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { TranslateService } from '../../../../../translate/translate.service';
import { NotificationService } from '../../../../../services/notification.service';
import { AbstractPageModalComponent, PageModalType } from '../abstract-page-modal.component';
import { $page } from '../../../../../../../shared/constants/page';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { NotificationPosition } from '../../../../../models/notification.model';
import { IPage, IPagesResponse } from '../../../../../../../shared/interfaces/page.interface';
import { IDigitalPropertyDetails } from '../../../../../../../shared/interfaces/digital-property.interface';
import { UserPropertyService } from '../../../../../services/user-property.service';
import { ICurrentSelectedProperty } from '../../../../../services/user.service';
import { DigitalPropertyService } from '../../../../../services/digital-property.service';

export interface IAddPageModalContext {
  workspaceId: string;
  digitalPropertyId: string;
  backNavLabel?: string;
  // defaults false
  requireURLField?: boolean;
  defaultURL?: string;
}

@Component({
  selector: 'app-add-page-modal',
  templateUrl: '../upsert-page-modal.component.html',
  standalone: false,
})
export class AddPageModalComponent extends AbstractPageModalComponent {
  public readonly modalType: PageModalType = PageModalType.ADD;
  private context: IAddPageModalContext;

  constructor(
    modal: NgbActiveOffcanvas,
    element: ElementRef<Element>,
    errorMessageService: ErrorMessageService,
    private pageService: PageService,
    private digitalPropertyService: DigitalPropertyService,
    private loadErrorHandler: LoadErrorHandler,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private userPropertyService: UserPropertyService,
  ) {
    super(modal, element, errorMessageService);
  }

  public init(context: IAddPageModalContext): void {
    this.context = context;
    this.backNavLabel = context.backNavLabel;

    this.formConfig = {
      requireUrl: context.requireURLField,
      urlEditable: true,
      initialUrl: context.defaultURL,
    };

    if (SharedCommonUtility.notNullishOrEmpty(context.defaultURL)) {
      this.subscription.add(
        this.userPropertyService
          .currentSelectedProperty()
          .pipe(
            map(({ digitalPropertyName }: ICurrentSelectedProperty): string => {
              return this.translateService.instant('add_pages_QOL_save_banner', digitalPropertyName);
            }),
          )
          .subscribe((text: string) => {
            this.bannerText = text;
          }),
      );
    }

    this.loadValidators();
  }

  private loadValidators(): void {
    this.subscription.add(
      this.pageService
        .getPages(this.context.workspaceId, this.context.digitalPropertyId)
        .subscribe((response: IPagesResponse): void => {
          this.existingUrls = response.pages.map((page: IPage) => page[$page.url]);
          this.existingNames = response.pages.map((page: IPage) => page[$page.name]);
        }),
    );

    this.subscription.add(
      this.digitalPropertyService
        .getDigitalProperty(this.context.workspaceId, this.context.digitalPropertyId)
        .subscribe((response: IDigitalPropertyDetails): void => {
          this.domainRestrictions = response.authorizedDomains.map((authorizedDomain: string) =>
            SharedCommonUtility.urlPatternToRegexp(authorizedDomain),
          );
        }),
    );
  }

  public submit(): void {
    if (!this.pageFormComponent.triggerValidation()) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    const formData: FormData = this.pageFormComponent.getFormData();

    this.subscription.add(
      this.pageService.createPage(this.context.workspaceId, this.context.digitalPropertyId, formData).subscribe({
        next: (createdPage: IPage) => {
          const message: string = this.translateService.instant('add_page_success');
          this.notificationService.success(message, NotificationPosition.Toast, false, AddPageModalComponent.name);
          this.modal.close(createdPage);
        },
        error: (err: HttpErrorResponse) => this.loadErrorHandler.handleError('add_page_failed', err, AddPageModalComponent.name),
      }),
    );
  }
}
